import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ActionIcon,
  Button,
  ComboboxItem,
  Group,
  Modal,
  ScrollAreaAutosize,
  Select,
  Tabs,
  Text,
  Textarea,
  TextInput,
} from "@mantine/core";
import { DateTimePicker } from "@mantine/dates";
import { IconClock } from "@tabler/icons-react";

import { PermissionGate } from "@components/PermissionGate";
import { RejectAppointmentModal } from "@components/RejectAppointmentModal";

import {
  showLoadingNotification,
  showSuccessNotification,
} from "@utils/showNotification";

import { useUpdateAppointment } from "@hooks/mutations/appointment/useUpdateAppointment";
import { useQueryErrorHandling } from "@hooks/useQueryErrorHandling";

import {
  Appointment,
  HomeCareDetail,
  UpdateIncomingAppointment,
} from "@customTypes/appointment";
import { BasicUser } from "@customTypes/user";

import { EditTeleconsultationAppointmentDialogProps } from "../EditTeleconsultationAppointmentModal";


export const EditHomecareAppointmentModal = ({
  selectedRow,
  doctors,
  variant,
  opened,
  close,
}: EditTeleconsultationAppointmentDialogProps) => {
  const { t } = useTranslation();

  const [appointmentDate, setAppointmentDate] = useState<Date | null>(null);
  const [doctor, setDoctor] = useState<ComboboxItem | null>(null);
  const [formData, setFormData] = useState<Partial<Appointment>>({});
  const [rejectModalOpened, setRejectModalOpened] = useState(false);

  const timePickerRef = useRef<HTMLInputElement>(null);

  const { mutate: updateAppointment, isLoading: isUpdateAppointmentLoading } =
    useUpdateAppointment();
  const handleErrors = useQueryErrorHandling();

  const handleUpdateAppointment = () => {
    const jsFormData = new FormData();
    jsFormData.append("doctor_assigned", doctor?.value ?? "");
    jsFormData.append("status", formData?.status ?? "");
    jsFormData.append("appointment_date", appointmentDate?.toISOString() ?? "");
    jsFormData.append(
      "details",
      JSON.stringify({ service_date: appointmentDate?.toISOString() ?? "" })
    );

    console.group("jsFormData");
    jsFormData.forEach((value, key) => {
      import.meta.env.DEV && console.log(`${key}: ${value}`);
    });
    console.groupEnd();

    const id = showLoadingNotification({});
    updateAppointment(
      {
        appointmentId: formData.id as string,
        formData: jsFormData,
      },
      {
        onSuccess: () => {
          showSuccessNotification({
            id,
            title: "success",
            message: "update-appointment-success",
          });
          close();
        },
        onError: (res: any) => {
          handleErrors(res, "mutation", id);
        },
      }
    );
  };

  useEffect(() => {
    if (selectedRow) {
      const {
        details,
        id,
        category,
        doctorAssigned,
        status,
        paymentTransaction,
        appointmentDate,
        medium,
        promoCode,
      } = selectedRow;

      setDoctor({
        label: (doctorAssigned as BasicUser)?.fullName,
        value: (doctorAssigned as BasicUser)?.id,
      });
      setAppointmentDate(new Date(appointmentDate));
      setFormData({
        id,
        status,
        doctorAssigned,
        category,
        details,
        paymentTransaction,
        medium,
        promoCode,
      });
    }
  }, [selectedRow]);

  useEffect(() => {
    if (appointmentDate) {
      setFormData(prevData => ({ ...prevData, date: appointmentDate }));
    }
  }, [appointmentDate]);

  return (
    <>
    <Modal
      centered
      size={"lg"}
      title={<Text fw={500}>{t("edit-homecare-appointment")}</Text>}
      opened={opened}
      onClose={close}
      scrollAreaComponent={ScrollAreaAutosize}
      styles={{ body: { minHeight: "auto" } }}>
      <Tabs defaultValue={"contact"}>
        <Tabs.List grow>
          <Tabs.Tab value="contact">{t("contact")}</Tabs.Tab>
          <Tabs.Tab value="appointment">{t("appointment")}</Tabs.Tab>
          <Tabs.Tab value="billing">{t("billing")}</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="contact">
          <form className="form">
            <div className="form-group">
              <TextInput
                label={t("name")}
                disabled
                value={
                  (formData.details as HomeCareDetail)?.firstName +
                  " " +
                  (formData.details as HomeCareDetail)?.surname
                }
              />
              <TextInput
                label={t("phone-number")}
                disabled
                value={(formData.details as HomeCareDetail)?.phoneNumber || ""}
              />
            </div>

            <div className="form-group">
              <TextInput
                label={t("gender")}
                disabled
                value={(formData.details as HomeCareDetail)?.gender || ""}
              />
              <TextInput
                label={t("location")}
                disabled
                value={(formData.details as HomeCareDetail)?.city || ""}
              />
            </div>
            <div className="form-group">
              <TextInput
                label={t("zone")}
                disabled
                value={(formData.details as HomeCareDetail)?.zone || ""}
              />
              <TextInput
                label={t("medium")}
                disabled
                value={formData?.medium || ""}
              />
            </div>
          </form>
        </Tabs.Panel>

        <Tabs.Panel value="appointment">
          <form className="form">
            <div className="form-group">
              <TextInput
                label={t("service-type")}
                disabled
                value={(formData.details as HomeCareDetail)?.serviceType || ""}
              />
              <DateTimePicker
                label={t("date-and-time")}
                value={appointmentDate}
                onChange={setAppointmentDate}
                timeInputProps={{
                  ref: timePickerRef,
                  leftSection: (
                    <ActionIcon
                      variant="subtle"
                      color="gray"
                      onClick={() => timePickerRef?.current?.showPicker()}>
                      <IconClock size={16} />
                    </ActionIcon>
                  ),
                  // minTime: "08:00:00",
                  // maxTime: "17:00:00",
                }}
              />
            </div>
            <div className="form-group">
              <Select
                label={t("doctor")}
                placeholder={t("select-doctor")}
                required
                data={doctors}
                value={
                  doctor?.value ??
                  (selectedRow.doctorAssigned as BasicUser)?.id ??
                  null
                }
                onChange={(_val, option) => {
                  setDoctor(option);
                }}
              />
              <TextInput
                label={t("price") + " (CFA)"}
                disabled
                value={(formData.details as HomeCareDetail)?.cost || ""}
              />
            </div>
            <Textarea
              label={t("additional-health-info")}
              disabled
              value={
                (formData.details as HomeCareDetail)?.additionalHealthInfo ||
                "N/A"
              }
              rows={3}
            />
          </form>
        </Tabs.Panel>

        <Tabs.Panel value="billing">
          <form className="form">
            <div className="form-group">
              <TextInput
                label={t("payment-method")}
                disabled
                value={
                  (formData.details as HomeCareDetail)?.paymentMethod || "N/A"
                }
              />
              <TextInput
                label={t("amount-paid")}
                disabled
                value={formData.paymentTransaction?.amountPaid || 0}
              />
            </div>

            <div className="form-group">
              <TextInput
                label={t("payer")}
                disabled
                value={
                  (formData.details as HomeCareDetail)?.insuranceType || "N/A"
                }
              />
              <TextInput
                label={t("policy-number")}
                disabled
                value={
                  (formData.details as HomeCareDetail)?.insuranceInfo || "N/A"
                }
              />
            </div>
            <div className="form-field">
              <TextInput
                w={"calc(50% - 0.8rem)"}
                label={t("promo-code")}
                disabled
                value={formData.promoCode || "N/A"}
              />
            </div>
          </form>
        </Tabs.Panel>

        <Group mt="xl" justify="end">
          <PermissionGate permission={variant === "incoming" ? "appointments.assign" : "appointments.edit"}><Button
            variant="light"
            color="red"
            disabled={isUpdateAppointmentLoading}
            onClick={() => {
              if (variant === "incoming") {
                setRejectModalOpened(true);
                return;
              }
              const id = showLoadingNotification({});
              const jsFormData = new FormData();
              jsFormData.append("status", "cancelled");

              updateAppointment(
                {
                  appointmentId: formData.id as string,
                  formData: jsFormData,
                },
                {
                  onSuccess: () => {
                    close();
                    showSuccessNotification({
                      id,
                      title: "success",
                    message: "cancel-appointment-success",
                    });
                  },
                  onError: res => {
                    handleErrors(res, "mutation", id);
                  },
                }
              );
            }}>
            {variant === "incoming"
              ? t("reject-request")
              : t("cancel-appointment")}
          </Button></PermissionGate>
          <PermissionGate permission="appointments.edit"><Button
            onClick={handleUpdateAppointment}
            disabled={
              !appointmentDate ||
              (!(selectedRow.doctorAssigned as BasicUser)?.id && !doctor?.value)
            }
            loading={isUpdateAppointmentLoading}>
            {t("update")}
          </Button></PermissionGate>
        </Group>
      </Tabs>
    </Modal>
    <RejectAppointmentModal
      opened={rejectModalOpened}
      close={() => setRejectModalOpened(false)}
      appointmentId={formData.id as string}
      onRejected={close}
    />
    </>
  );
};
